import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';

interface AssetType {
  asset_type_id: string;
  type_name: string;
  description: string;
  business_name: string;
  is_global: boolean;
  created_by_owner: boolean;
}

const GlobalAssetTypeManagement: React.FC = () => {
  const [assetTypes, setAssetTypes] = useState<AssetType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchAssetTypes();
  }, []);

  const fetchAssetTypes = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication error: No token found. Please log in again.');
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/asset-types/all-businesses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data && response.data.success && Array.isArray(response.data.data)) {
        setAssetTypes(response.data.data);
      } else if (response.data && Array.isArray(response.data)) {
        setAssetTypes(response.data);
      } else {
        setAssetTypes([]);
      }
    } catch (err: any) {
      console.error('Error fetching asset types:', err);
      
      if (err.response?.status === 401) {
        setError('Authentication failed. Please log in again with Owner/Super Admin credentials.');
      } else if (err.response?.status === 403) {
        setError('You do not have permission to view asset types from all businesses. Make sure you are logged in as an Owner or Super Admin.');
      } else if (err.response?.status === 404) {
        setError('Asset types endpoint not found. Please check your backend configuration.');
      } else {
        setError(`Failed to fetch asset types: ${err.response?.data?.message || err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleToggleGlobal = async (assetTypeId: string, currentGlobal: boolean) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('No authentication token found');
        return;
      }

      // Use the new dedicated global status endpoint
      const response = await axios.put(
        `${API_BASE_URL}/asset-types/${assetTypeId}/global`,
        { is_global: !currentGlobal },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log('Toggle global response:', response.data);
      
      // Refresh asset types
      fetchAssetTypes();
    } catch (err: any) {
      console.error('Error toggling global status:', err);
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Failed to update asset type global status. Please try again.');
      }
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main', mb: 3 }}>
        Global Asset Type Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 4, p: 3, borderRadius: 2, boxShadow: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontFamily: 'Roboto' }}>Asset Types</Typography>
        </Box>
        
        <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none' }}>
          <Table>
            <TableHead sx={{ bgcolor: 'background.default' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Asset Type Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Business</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">Global</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {assetTypes.length > 0 ? (
                assetTypes.map((assetType) => (
                  <TableRow key={assetType.asset_type_id} hover sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                    <TableCell>
                      <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                        {assetType.type_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {assetType.description || 'No description'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {assetType.business_name || 'Unknown Business'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={assetType.is_global}
                            onChange={() => handleToggleGlobal(assetType.asset_type_id, assetType.is_global)}
                            color="primary"
                            sx={{ 
                              '& .MuiSvgIcon-root': { fontSize: 20 },
                              py: 0.5,
                            }}
                          />
                        }
                        label={
                          <Typography variant="body2" sx={{ fontWeight: assetType.is_global ? 600 : 400 }}>
                            Global
                          </Typography>
                        }
                        sx={{ 
                          m: 0,
                          '& .MuiFormControlLabel-label': {
                            color: assetType.is_global ? 'primary.main' : 'text.secondary'
                          }
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No asset types found.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Asset types will appear here once businesses create them.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Container>
  );
};

export default GlobalAssetTypeManagement; 